'use client'

import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Icon } from '@/payload-types'
import { IconComponent } from '@/components/ui/icon'
import { Card as UiCard, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'

export type RenderedCardItem = {
  id?: string
  type?: 'divider' | 'richtext' | 'collapsible' | 'link' | 'search'
  title?: string
  description?: string
  divider?: string
  placeholder?: string
  icon?: string | Icon | null
  href?: string
  content?: React.ReactNode
}

export type CardBlockClientProps = {
  cardStyle?: 'default' | 'row-1' | 'row-2' | 'row-3' | 'row-4' | 'row-5'
  title?: string
  description?: string
  icon?: string | Icon | null
  collapsible?: boolean
  cardItems?: RenderedCardItem[]
}

const cardRowBackgroundStyle: Record<'row-1' | 'row-2' | 'row-3' | 'row-4' | 'row-5', React.CSSProperties> = {
  'row-1': { backgroundColor: 'var(--row-1-bg)' },
  'row-2': { backgroundColor: 'var(--row-2-bg)' },
  'row-3': { backgroundColor: 'var(--row-3-bg)' },
  'row-4': { backgroundColor: 'var(--row-4-bg)' },
  'row-5': { backgroundColor: 'var(--row-5-bg)' },
}

function normalize(value?: string) {
  return (value || '').trim().toLowerCase()
}

function itemBorderClass(index: number) {
  return index === 0 ? "border-t-0" : "theme-card-divider border-t"
}

export function CardBlockClient({ fields }: { fields: CardBlockClientProps }) {
  const [query, setQuery] = useState('')

  const cardStyle = fields.cardStyle || 'default'
  const items = Array.isArray(fields.cardItems) ? fields.cardItems : []
  const isCardCollapsible = Boolean(fields.collapsible)
  const hasHeaderContent = Boolean(normalize(fields.title) || normalize(fields.description))
  const cardBackgroundStyle = cardStyle !== 'default' ? cardRowBackgroundStyle[cardStyle] : undefined
  const normalizedQuery = normalize(query)
  const searchPlaceholder = items.find((item) => item.type === 'search')?.placeholder || 'Search card items...'

  const visibleItems = !normalizedQuery
    ? items
    : items.filter((item) => {
      if (item.type === 'search') return true
      return normalize(item.title).includes(normalizedQuery)
    })

  const header = hasHeaderContent ? (
    <CardHeader className={cn('flex flex-row items-center gap-3', isCardCollapsible && 'cursor-pointer')}>
      {fields.icon && <IconComponent icon={fields.icon} className="h-10 w-10 shrink-0" />}
      <div className="min-w-0 flex-1">
        {fields.title && <CardTitle>{fields.title}</CardTitle>}
        {fields.description && <CardDescription>{fields.description}</CardDescription>}
      </div>
      {isCardCollapsible && (
        <ChevronDown className="ml-auto h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
      )}
    </CardHeader>
  ) : null

  const body = (
    <CardContent className="p-0">
      {visibleItems.map((item, index) => {
        const key = item.id || `${item.type || 'item'}-${index}`

        if (item.type === 'search') {
          return (
            <div key={key} className={cn("px-4 py-3", itemBorderClass(index))}>
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            </div>
          )
        }

        if (item.type === 'divider') {
          return (
            <div key={key} className={cn("px-4 py-2 text-sm font-semibold uppercase tracking-wide", itemBorderClass(index))}>
              {item.divider || ''}
            </div>
          )
        }

        if (item.type === 'link') {
          return (
            <a
              key={key}
              href={item.href || '#'}
              className={cn(
                "theme-card-divider theme-card-content flex flex-row items-center gap-4 px-5 py-3.5 no-underline transition-opacity hover:opacity-90",
                itemBorderClass(index),
              )}
            >
              {item.icon && <IconComponent icon={item.icon} className="link-icon h-8 w-8 shrink-0" />}
              <div>
                <div className="text-[length:var(--button-font-size-mobile)] font-[var(--button-font-weight)] [text-transform:var(--button-text-transform,none)] md:text-[length:var(--button-font-size-desktop)]">{item.title}</div>
                {item.description && (
                  <div className="theme-card-accent">{item.description}</div>
                )}
              </div>
            </a>
          )
        }

        if (item.type === 'collapsible') {
          return (
            <Collapsible key={key} defaultOpen={false} className={itemBorderClass(index)}>
              <CollapsibleTrigger asChild>
                <div className="group flex w-full cursor-pointer items-center gap-3 p-4 md:p-6 transition-colors hover:bg-muted/50">
                  {item.icon && <IconComponent icon={item.icon} className="h-8 w-8 shrink-0" />}
                  {item.title ? (
                    <div className="theme-card-title min-w-0 flex-1 font-semibold">{item.title}</div>
                  ) : (
                    <div className="min-w-0 flex-1" />
                  )}
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=closed]:-rotate-90" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {item.content ? <div className="px-4 pb-4 pt-0 md:px-6 md:pb-6">{item.content}</div> : null}
              </CollapsibleContent>
            </Collapsible>
          )
        }

        const hasTitleRow = Boolean(item.icon || item.title)

        return (
          <div key={key} className={cn("space-y-2 p-4 md:p-6", itemBorderClass(index))}>
            {hasTitleRow ? (
              <div className="flex items-center gap-3">
                {item.icon && <IconComponent icon={item.icon} className="h-8 w-8 shrink-0" />}
                {item.title ? (
                  <div className="theme-card-title min-w-0 flex-1 font-semibold">{item.title}</div>
                ) : null}
              </div>
            ) : null}
            {item.content ?? null}
          </div>
        )
      })}
    </CardContent>
  )

  return (
    <div className="w-full">
      {isCardCollapsible && hasHeaderContent ? (
        <Collapsible defaultOpen={true} className="w-full">
          <UiCard className="text-left" style={cardBackgroundStyle}>
            <CollapsibleTrigger asChild>
              <div className="group">{header}</div>
            </CollapsibleTrigger>
            <CollapsibleContent>{body}</CollapsibleContent>
          </UiCard>
        </Collapsible>
      ) : (
        <UiCard className="text-left" style={cardBackgroundStyle}>
          {header}
          {body}
        </UiCard>
      )}
    </div>
  )
}
